import { useContext, useRef, useState } from "react";
import { AppContext } from "../../../../App";
import Swal from "sweetalert2";
import { sendSupportTicket } from "../services/support.services";
import { toast } from "react-toastify";
import { groupPractices } from "../../Connections/connections.middleware";
import * as auth from "../../../../util/auth";
import usePracticesByClientSelected from "../../Connections/components/PracticesByClientSelected/hooks/usePracticesByClientSelected";

const getSupportTicketLabel = (responseValue) => {
  return responseValue?.value?.ticket?.supportTicket?.ticketId || "ticket";
};

const escapeHtml = (value) => {
  return String(value)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");
};

export const useSupport = () => {
  const appData = useContext(AppContext);
  const practices = appData.get("practices") || [];
  const [practiceSelected, setPracticeSelected] = useState(null);
  const [categorySelected, setCategorySelected] = useState(null);
  const [formInputs, setFormInputs] = useState({
    ticketName: "",
    description: "",
    url: "",
  });
  const [isLoading, setIsLoading] = useState(false);
  const [files, setFiles] = useState([]);
  const inputRef = useRef(null);
  const { client } = usePracticesByClientSelected();
  const userInfo = auth.getLoggedInData();

  let obj = groupPractices(practices);

  const _allPractices = Object.values(obj);
  const firstClient = _allPractices.find(
    (item) => item.clientId === client.clientId,
  );

  const practiceOptions =
    firstClient?.practices?.map((item) => {
      return {
        label: item.name,
        value: item.name,
      };
    }) ?? [];

  const handleClick = () => {
    inputRef.current.click();
  };

  const handleFiles = (fileList) => {
    const newFiles = Array.from(fileList);
    const maxSize = 10 * 1024 * 1024;

    const validFiles = newFiles.filter((file) => {
      if (file.size > maxSize) {
        toast.warn("Some files exceed the 10 MB limit.");
        return false;
      }
      return true;
    });

    setFiles((prev) => [...prev, ...validFiles]);
  };

  const handleChange = (e) => {
    handleFiles(e.target.files);
  };

  const handleRemoveFile = (indexToRemove) => {
    setFiles((prev) => prev.filter((_, index) => index !== indexToRemove));
    if (inputRef.current) {
      inputRef.current.value = "";
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    handleFiles(e.dataTransfer.files);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  function handleCancel() {
    setFormInputs({ ticketName: "", description: "", url: "" });
    setCategorySelected(null);
    setPracticeSelected(null);
    setFiles([]);
    if (inputRef.current) {
      inputRef.current.value = "";
    }
  }

  async function handleSubmit(e) {
    e.preventDefault();

    if (!practiceSelected) {
      Swal.fire({
        text: "Select an office or clinic name to submit the ticket",
        icon: "error",
      });
      return;
    }

    if (!categorySelected) {
      Swal.fire({
        text: "Select a request type to submit the ticket",
        icon: "error",
      });
      return;
    }

    setIsLoading(true);

    const user = userInfo?.firstName + " " + userInfo?.lastName;
    const email = userInfo?.name ?? "";

    const formData = new FormData();
    const location = practiceSelected
      .map((practice) => practice.label)
      .join(", ");

    formData.append("location", location);
    formData.append("subject", formInputs.ticketName);
    formData.append("priority", "3");
    formData.append(
      "requestType",
      categorySelected ? categorySelected.value : "",
    );
    formData.append("description", formInputs.description);
    formData.append("userEmail", email);
    formData.append("userName", user);
    files.forEach((file) => {
      formData.append("files[]", file);
    });

    await sendSupportTicket(formData, firstClient?.clientId ?? "")
      .then((response) => {
        const [, responseValue] = response;

        if (
          responseValue &&
          "success" in responseValue &&
          responseValue.success === false
        ) {
          toast.error("Request failed, please try again");
        } else {
          const ticketLabel = escapeHtml(getSupportTicketLabel(responseValue));
          handleCancel();
          Swal.fire({
            html: `
              <div class="support-success-modal">
                <div class="support-success-icon">✓</div>
                <h3 class="support-success-title">Ticket sent successfully</h3>
                <p>Our team will review your request and follow up with you via email as soon as possible.</p>
                <div class="support-success-note">Providing complete information helps us resolve your request more efficiently.</div>
                <div class="support-success-ticket">
                  <span>TICKET:</span>
                  <strong>${ticketLabel}</strong>
                </div>
              </div>
            `,
            showConfirmButton: true,
            confirmButtonText: "Agree",
            buttonsStyling: false,
            customClass: {
              popup: "support-success-popup",
              actions: "support-success-actions",
              confirmButton: "support-success-confirm",
            },
          });
        }
      })
      .catch((error) => {
        console.error(error);
        toast.error("Request failed, please try again");
      })
      .finally(() => setIsLoading(false));
  }

  return {
    inputRef,
    handleClick,
    handleFiles,
    handleChange,
    handleRemoveFile,
    handleDrop,
    handleDragOver,
    practiceSelected,
    setPracticeSelected,
    practiceOptions,
    formInputs,
    setFormInputs,
    categorySelected,
    setCategorySelected,
    handleSubmit,
    handleCancel,
    files,
    isLoading,
  };
};
